import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { scheduledScansApi, type ScheduledScanRun } from '../../services/scheduledScansApi';
import { resolveScheduledRunScanOutcome, scheduledRunCreatedScanHref } from '../../domain/safetyCases/scheduledRunScanOutcome';
import { formatSiteDateTime } from '../../domain/siteTime/siteTime';
import { useSiteTimeZone } from '../../hooks/useSiteTimeZone';

function statusColor(status: string): 'default' | 'success' | 'warning' | 'error' {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
    case 'CANCELLED':
      return 'error';
    case 'INSUFFICIENT_COVERAGE':
    case 'DEFERRED':
    case 'QUEUED':
    case 'RUNNING':
      return 'warning';
    default:
      return 'default';
  }
}

function canCancel(run: ScheduledScanRun): boolean {
  return ['EXPECTED', 'QUEUED', 'DEFERRED'].includes(run.status);
}

function canRetry(run: ScheduledScanRun): boolean {
  return run.status === 'FAILED';
}

function formatDate(value?: string | null, timeZone?: string | null): string {
  if (!value) return 'Not available';
  return formatSiteDateTime(value, timeZone) || 'Not available';
}

export default function ScheduledScanRunHistoryCard({
  orgId,
  siteId,
  definitionId,
  definitionName,
  canOperate,
}: {
  orgId: string;
  siteId: string;
  definitionId: string;
  definitionName: string;
  canOperate: boolean;
}) {
  const navigate = useNavigate();
  const siteTimeZone = useSiteTimeZone(siteId);
  const [runs, setRuns] = useState<ScheduledScanRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [selectedRun, setSelectedRun] = useState<ScheduledScanRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionRunId, setActionRunId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadRuns = useCallback(async () => {
    if (!definitionId) {
      setRuns([]);
      setSelectedRunId(null);
      setSelectedRun(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const items = await scheduledScansApi.listDefinitionRuns(siteId, definitionId);
      setRuns(items);
      setSelectedRunId((current) =>
        current && items.some((item) => item.id === current) ? current : null,
      );
    } catch (cause: any) {
      setError(cause?.response?.data?.detail || cause?.message || 'Unable to load run history');
    } finally {
      setLoading(false);
    }
  }, [definitionId, siteId]);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedRunId) {
      setSelectedRun(null);
      return;
    }
    setDetailLoading(true);
    scheduledScansApi
      .getRun(siteId, selectedRunId)
      .then((run) => {
        if (!cancelled) setSelectedRun(run);
      })
      .catch((cause: any) => {
        if (!cancelled) setError(cause?.response?.data?.detail || cause?.message || 'Unable to load run detail');
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRunId, siteId]);

  const selectedSummary = useMemo(
    () => runs.find((item) => item.id === selectedRunId) || null,
    [runs, selectedRunId],
  );
  const selectedOutcome = resolveScheduledRunScanOutcome(selectedRun || selectedSummary);
  const createdScanHref = selectedOutcome.scanCreated
    ? scheduledRunCreatedScanHref({ siteId, reportId: selectedOutcome.createdScanId })
    : null;

  const mutateRun = async (run: ScheduledScanRun, action: 'cancel' | 'retry') => {
    setActionRunId(run.id);
    setError(null);
    try {
      const next =
        action === 'cancel'
          ? await scheduledScansApi.cancelRun(siteId, run.id, 'operator_cancel_from_ui')
          : await scheduledScansApi.retryRun(siteId, run.id, 'operator_retry_from_ui');
      setSelectedRun(next);
      await loadRuns();
      setSelectedRunId(next.id);
    } catch (cause: any) {
      setError(cause?.response?.data?.detail || cause?.message || `Unable to ${action} run`);
    } finally {
      setActionRunId(null);
    }
  };

  return (
    <Card variant="outlined" data-testid="scheduled-scan-run-history" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 750 }}>
              Run History
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {definitionName
                ? `History for ${definitionName}.`
                : 'Scheduled occurrence history for the selected Scan definition.'}
            </Typography>
          </Box>
          {error ? <Alert severity="warning">{error}</Alert> : null}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress aria-label="Loading scheduled scan run history" />
            </Box>
          ) : runs.length === 0 ? (
            <Alert severity="info">No scheduled runs have been materialized for this definition yet.</Alert>
          ) : (
            <>
              <TableContainer>
                <Table size="small" aria-label="Scheduled Scan run history">
                  <TableHead>
                    <TableRow>
                      <TableCell>Occurrence</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Scan outcome</TableCell>
                      <TableCell>Window</TableCell>
                      <TableCell>Digest</TableCell>
                      <TableCell>Retry</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {runs.map((run) => (
                      <TableRow
                        key={run.id}
                        selected={run.id === selectedRunId}
                        hover
                        onClick={() => setSelectedRunId(run.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatDate(run.occurrence_at_utc, siteTimeZone)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {run.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color={statusColor(run.status)} label={run.status} />
                        </TableCell>
                        <TableCell>
                          {resolveScheduledRunScanOutcome(run).scanOutcomeLabel}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(run.window_start_utc, siteTimeZone)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            to {formatDate(run.window_end_utc, siteTimeZone)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {run.notification ? (
                            <Chip size="small" variant="outlined" label={run.notification.status} />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              None
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{run.retry_count}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {canOperate && canCancel(run) ? (
                              <Button
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void mutateRun(run, 'cancel');
                                }}
                                disabled={actionRunId === run.id}
                              >
                                Cancel
                              </Button>
                            ) : null}
                            {canOperate && canRetry(run) ? (
                              <Button
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void mutateRun(run, 'retry');
                                }}
                                disabled={actionRunId === run.id}
                              >
                                Retry
                              </Button>
                            ) : null}
                            <Button
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedRunId(run.id);
                              }}
                            >
                              Details
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {selectedRunId ? (
                <Box
                  data-testid="scheduled-scan-run-detail"
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}
                >
                  {detailLoading && !selectedRun ? (
                    <CircularProgress size={20} aria-label="Loading scheduled run detail" />
                  ) : selectedRun || selectedSummary ? (
                    <Stack spacing={1.25}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Run Detail
                      </Typography>
                      <Typography variant="body2">
                        Terminal reason: {selectedOutcome.scanOutcomeReason || 'None'}
                      </Typography>
                      <Typography variant="body2">
                        Pipeline run: <code>{selectedRun?.links.pipeline_run_id || selectedSummary?.links.pipeline_run_id || 'n/a'}</code>
                      </Typography>
                      <Typography variant="body2">
                        Coverage result: <code>{selectedRun?.links.coverage_result_id || selectedSummary?.links.coverage_result_id || 'n/a'}</code>
                      </Typography>
                      <Typography variant="body2">
                        Result summary: <code>{selectedRun?.links.result_summary_id || selectedSummary?.links.result_summary_id || 'n/a'}</code>
                      </Typography>
                      <Typography variant="body2" data-testid="scheduled-scan-run-scan-outcome">
                        Safety Scan outcome: {selectedOutcome.scanOutcomeLabel}
                      </Typography>
                      <Typography variant="body2">
                        Digest status: {selectedRun?.notification?.status || selectedSummary?.notification?.status || 'None'}
                      </Typography>
                      <Typography variant="body2">
                        App version: {selectedRun?.configuration?.application_version || 'Not captured'}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {createdScanHref ? (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => navigate(createdScanHref)}
                            data-testid="scheduled-scan-run-open-scan"
                          >
                            Open Safety Scan
                          </Button>
                        ) : null}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/acceptance/reports?org_id=${encodeURIComponent(orgId)}&site_id=${encodeURIComponent(siteId)}`)}
                        >
                          Open reports surface
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/incidents?org_id=${encodeURIComponent(orgId)}&site_id=${encodeURIComponent(siteId)}&table_mode=grouped`)}
                        >
                          Open incidents review
                        </Button>
                      </Stack>
                    </Stack>
                  ) : null}
                </Box>
              ) : null}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
